import { Injectable, inject } from '@angular/core';
import { AuthSession, SupabaseClient } from '@supabase/supabase-js';
import { BehaviorSubject } from 'rxjs';
import { User } from '../../model/user';
import { UserService } from '../user-service/user.service';
import { AuthService } from '../auth-service/auth.service';
import { SUPABASE_CLIENT } from '@app/providers/supabase.provider';

/** 快速切換用：該帳號在這台裝置上自己的 Supabase session */
export interface SavedSession {
  access_token: string;
  refresh_token: string;
  /** 保存期限，過期後需要重新輸入密碼 */
  expires_at: number;
}

export interface SavedAccount {
  id: string;
  phone: string;
  name: string;
  lastLoginTime: number;
  avatar?: string;
  savedSession?: SavedSession;
}

// 舊版曾保存可還原的密碼，載入時一律移除
const LEGACY_CREDENTIAL_FIELDS = ['credentialBlob', 'credentialExpiry', 'encryptedToken', 'tokenExpiry'] as const;

@Injectable({
  providedIn: 'root'
})
export class AccountManagerService {
  private readonly SAVED_ACCOUNTS_KEY = 'timeBank_savedAccounts';
  private readonly CURRENT_ACCOUNT_KEY = 'timeBank_currentAccount';
  private readonly QUICK_SWITCH_VALIDITY_HOURS = 168; // 快速切換有效期（小時）- 7天
  private readonly MAX_SAVED_ACCOUNTS = 5;
  private readonly UNCONFIRMED_ACCOUNT_ERROR = '你的帳號尚未驗證或發生錯誤，相關解決辦法請聯繫管理員！';

  private supabaseClient: SupabaseClient;
  private _savedAccounts$ = new BehaviorSubject<SavedAccount[]>([]);
  private _currentAccount$ = new BehaviorSubject<SavedAccount | null>(null);
  private _isAccountSwitching$ = new BehaviorSubject<boolean>(false);

  public savedAccounts$ = this._savedAccounts$.asObservable();
  public currentAccount$ = this._currentAccount$.asObservable();
  public isAccountSwitching$ = this._isAccountSwitching$.asObservable();

  constructor(
    private userService: UserService,
    private authService: AuthService
  ) {
    this.supabaseClient = inject(SUPABASE_CLIENT);
    this.loadSavedAccounts();

    // Supabase 每次換 token 都會讓舊 refresh token 失效，保存的 session 必須跟著更新
    this.supabaseClient.auth.onAuthStateChange((event, session) => {
      if ((event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') && session) {
        this.updateSavedSessionTokens(session);
      }
    });
  }

  // 切換到指定帳號（輸入密碼）；原本的帳號不登出，之後仍可快速切回
  async switchToAccount(account: SavedAccount, password: string): Promise<{success: boolean, error?: string}> {
    try {
      this._isAccountSwitching$.next(true);

      const loginResult = await this.authService.signInWithMultiDevice(account.phone, password);
      if (!loginResult || loginResult.error) {
        console.error(' 登入失敗:', loginResult?.error?.message);
        return { success: false, error: loginResult?.error?.message || '登入失敗' };
      }

      return await this.completeSwitch(account);
    } catch (error: any) {
      console.error(' 帳號切換過程中發生錯誤:', error);
      return { success: false, error: error.message };
    } finally {
      this._isAccountSwitching$.next(false);
    }
  }

  // 快速切換：還原該帳號自己保存的 session，不重新登入也不保存密碼
  async quickSwitchToAccount(account: SavedAccount): Promise<{success: boolean, needPassword: boolean, error?: string}> {
    if (!this.canQuickSwitch(account) || !account.savedSession) {
      return { success: false, needPassword: true };
    }

    try {
      this._isAccountSwitching$.next(true);

      const { error } = await this.supabaseClient.auth.setSession({
        access_token: account.savedSession.access_token,
        refresh_token: account.savedSession.refresh_token
      });

      if (error) {
        // 該 session 已在別處登出或過期，改為要求輸入密碼
        this.forgetAccountCredential(account.phone);
        return { success: false, needPassword: true, error: '認證資訊已過期，需要重新輸入密碼' };
      }

      const result = await this.completeSwitch(account);
      return { ...result, needPassword: false };
    } catch (error: any) {
      return { success: false, needPassword: true, error: error.message };
    } finally {
      this._isAccountSwitching$.next(false);
    }
  }

  // 檢查帳號是否支持快速切換
  canQuickSwitch(account: SavedAccount): boolean {
    return !!account.savedSession && account.savedSession.expires_at > Date.now();
  }

  // 檢查帳號是否有有效的快速切換 session
  hasValidToken(account: SavedAccount): boolean {
    return this.canQuickSwitch(account);
  }

  // 獲取快速切換剩餘有效時間（分鐘）
  getTokenRemainingTime(account: SavedAccount): number {
    if (!account.savedSession) {
      return 0;
    }

    const remaining = account.savedSession.expires_at - Date.now();
    return Math.max(0, Math.floor(remaining / (60 * 1000)));
  }

  /**
   * 延長指定帳號的快速切換有效期。
   * 保留舊方法名稱，避免呼叫端需要跟著改名。
   */
  public refreshTokenForAccount(phone: string): boolean {
    const account = this.findAccount(phone);
    if (!account?.savedSession) {
      return false;
    }

    this.updateSavedAccount({
      ...account,
      savedSession: { ...account.savedSession, expires_at: this.generateQuickSwitchExpiry() }
    });
    return true;
  }

  /**
   * 保存帳號到列表。
   * @param rememberSession 為 true 時保存目前的 session 供快速切換
   */
  async saveAccount(user: User, phone: string, rememberSession = false) {
    const existing = this.findAccount(phone);
    const savedSession = rememberSession ? await this.captureCurrentSession(user.id) : undefined;
    const savedAccount: SavedAccount = {
      id: user.id,
      phone: phone,
      name: user.name,
      lastLoginTime: Date.now(),
      avatar: user.avatar_url,
      savedSession: savedSession ?? (rememberSession ? existing?.savedSession : undefined)
    };

    const accounts = this._savedAccounts$.value.filter(acc => this.normalizePhone(acc.phone) !== this.normalizePhone(phone));
    accounts.push(savedAccount);
    if (accounts.length > this.MAX_SAVED_ACCOUNTS) {
      accounts.splice(0, accounts.length - this.MAX_SAVED_ACCOUNTS);
    }

    this.persistAccounts(accounts);
    this.setCurrentAccount(savedAccount);
  }

  // 移除保存的帳號
  removeAccount(phone: string) {
    const accounts = this._savedAccounts$.value.filter(acc => acc.phone !== phone);
    this.persistAccounts(accounts);

    const current = this._currentAccount$.value;
    if (current?.phone === phone) {
      localStorage.removeItem(this.CURRENT_ACCOUNT_KEY);
      this._currentAccount$.next(null);
    }
  }

  // 忘記指定帳號的快速切換 session，但保留帳號在列表中
  forgetAccountCredential(phone: string) {
    const account = this.findAccount(phone);
    if (!account) {
      return;
    }

    this.updateSavedAccount({ ...account, savedSession: undefined });
  }

  // 清除所有保存的帳號
  clearAllAccounts() {
    localStorage.removeItem(this.SAVED_ACCOUNTS_KEY);
    localStorage.removeItem(this.CURRENT_ACCOUNT_KEY);
    this._savedAccounts$.next([]);
    this._currentAccount$.next(null);
  }

  // 設置當前帳號
  setCurrentAccount(account: SavedAccount) {
    account.lastLoginTime = Date.now();
    localStorage.setItem(this.CURRENT_ACCOUNT_KEY, JSON.stringify(account));
    this._currentAccount$.next(account);
  }

  // 獲取保存的帳號列表
  getSavedAccounts(): SavedAccount[] {
    return this._savedAccounts$.value;
  }

  // 獲取當前帳號
  getCurrentAccount(): SavedAccount | null {
    return this._currentAccount$.value;
  }

  // 切換後共用的收尾：確認審核狀態、更新帳號資料與保存的 session
  private async completeSwitch(account: SavedAccount): Promise<{ success: boolean, error?: string }> {
    const isConfirmed = await this.userService.checkIsConfirm();
    if (!isConfirmed) {
      await this.authService.signOut();
      return { success: false, error: this.UNCONFIRMED_ACCOUNT_ERROR };
    }

    const userInfo = await this.userService.getUser();
    const user = userInfo?.data?.[0];
    if (!user) {
      console.warn(' 無法獲取用戶詳細資訊');
      return { success: true };
    }

    const savedSession = await this.captureCurrentSession(user.id);
    const updatedAccount: SavedAccount = {
      ...account,
      id: user.id,
      lastLoginTime: Date.now(),
      avatar: user.avatar_url || account.avatar,
      savedSession: savedSession ?? account.savedSession
    };

    this.setCurrentAccount(updatedAccount);
    this.updateSavedAccount(updatedAccount);
    return { success: true };
  }

  private async captureCurrentSession(userId: string): Promise<SavedSession | undefined> {
    const session = await this.authService.getSession();
    if (!session || session.user.id !== userId) {
      return undefined;
    }

    return {
      access_token: session.access_token,
      refresh_token: session.refresh_token,
      expires_at: this.generateQuickSwitchExpiry()
    };
  }

  private updateSavedSessionTokens(session: AuthSession) {
    const accounts = this._savedAccounts$.value;
    const index = accounts.findIndex(acc => acc.id === session.user.id && acc.savedSession);
    if (index < 0) {
      return;
    }

    const savedSession = accounts[index].savedSession!;
    if (savedSession.refresh_token === session.refresh_token && savedSession.access_token === session.access_token) {
      return;
    }

    accounts[index] = {
      ...accounts[index],
      savedSession: {
        ...savedSession,
        access_token: session.access_token,
        refresh_token: session.refresh_token
      }
    };
    this.persistAccounts(accounts);
  }

  private generateQuickSwitchExpiry(): number {
    return Date.now() + (this.QUICK_SWITCH_VALIDITY_HOURS * 60 * 60 * 1000);
  }

  // 載入已保存的帳號列表，並移除舊版保存的密碼憑證
  private loadSavedAccounts() {
    if (typeof localStorage === 'undefined') {
      return;
    }

    let accounts: SavedAccount[] = [];
    try {
      const saved = localStorage.getItem(this.SAVED_ACCOUNTS_KEY);
      accounts = saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('讀取保存的帳號失敗:', error);
    }

    let hasLegacyCredential = false;
    const sanitized = accounts.map(account => {
      const copy: Record<string, unknown> = { ...account };
      for (const field of LEGACY_CREDENTIAL_FIELDS) {
        if (field in copy) {
          hasLegacyCredential = true;
          delete copy[field];
        }
      }
      return copy as unknown as SavedAccount;
    });

    if (hasLegacyCredential) {
      this.persistAccounts(sanitized);
    } else {
      this._savedAccounts$.next(sanitized);
    }

    try {
      const current = localStorage.getItem(this.CURRENT_ACCOUNT_KEY);
      if (current) {
        const currentAccount: Record<string, unknown> = JSON.parse(current);
        for (const field of LEGACY_CREDENTIAL_FIELDS) {
          delete currentAccount[field];
        }
        this._currentAccount$.next(currentAccount as unknown as SavedAccount);
        if (hasLegacyCredential) {
          localStorage.setItem(this.CURRENT_ACCOUNT_KEY, JSON.stringify(currentAccount));
        }
      }
    } catch (error) {
      console.error('讀取目前帳號失敗:', error);
    }
  }

  private findAccount(phone: string): SavedAccount | undefined {
    const normalizedPhone = this.normalizePhone(phone);
    return this._savedAccounts$.value.find(acc => this.normalizePhone(acc.phone) === normalizedPhone);
  }

  // 更新保存的帳號資訊
  private updateSavedAccount(account: SavedAccount) {
    const accounts = [...this._savedAccounts$.value];
    const normalizedPhone = this.normalizePhone(account.phone);
    const index = accounts.findIndex(acc => this.normalizePhone(acc.phone) === normalizedPhone);

    if (index >= 0) {
      accounts[index] = account;
    } else {
      accounts.push(account);
    }

    this.persistAccounts(accounts);

    const current = this._currentAccount$.value;
    if (current && this.normalizePhone(current.phone) === normalizedPhone) {
      localStorage.setItem(this.CURRENT_ACCOUNT_KEY, JSON.stringify(account));
      this._currentAccount$.next(account);
    }
  }

  private persistAccounts(accounts: SavedAccount[]) {
    localStorage.setItem(this.SAVED_ACCOUNTS_KEY, JSON.stringify(accounts));
    this._savedAccounts$.next(accounts);
  }

  private normalizePhone(phone: string): string {
    if (!phone) return '';
    let normalized = phone.replace(/[\s\-\(\)]/g, '');
    if (normalized.startsWith('0')) {
      normalized = '+886' + normalized.substring(1);
    }
    return normalized;
  }
}
